import TileWMS from 'ol/source/TileWMS.js'
import TileLayer from 'ol/layer/Tile.js'
import store from '@/store.js'
import { fetchWithTimeout, parseXmlOrThrow, checkServiceException, extractBBox, extractInfoFormat } from './ogcXml'

const CAPS_TIMEOUT_MS = 10000
const FEATURE_INFO_TIMEOUT_MS = 10000
const WMS_HIT_BUFFER_PX = 16 // pixels expandidos alrededor del clic para GetFeatureInfo

export const wmsConnector = {
  id: 'wms-external',
  serviceType: 'WMS',
  label: 'WMS externo',

  matchesUrl() {
    return false
  },

  async detect(baseUrl) {
    const capsUrl = `${baseUrl}?service=WMS&request=GetCapabilities&version=1.1.1`
    const res = await fetchWithTimeout(capsUrl, CAPS_TIMEOUT_MS)
    const txt = await res.text()
    const xml = parseXmlOrThrow(txt)
    checkServiceException(xml)

    const layers = [...xml.querySelectorAll('Layer > Name')].map(n => ({
      name: n.textContent,
      title: n.parentElement.querySelector(':scope > Title')?.textContent || n.textContent,
      bbox: extractBBox(n.parentElement, false),
      meta: {}
    }))
    if (!layers.length) {
      throw new Error('No se encontraron capas (revisa la URL o los permisos CORS del servidor).')
    }

    const infoFormat = extractInfoFormat(xml)
    layers.forEach(l => { l.meta.infoFormat = infoFormat })
    return layers
  },

  createOlLayer(layerConfig) {
    // Capa WMS agregada por URL en sesión: el nombre de capa ya viene completo
    // (tal como lo entrega GetCapabilities), sin split workspace:datastore.
    // No se asume serverType porque el servidor WMS externo es arbitrario.
    const source = new TileWMS({
      url: layerConfig.provider.url,
      params: {
        'LAYERS': layerConfig.provider.layerName,
        'VERSION': '1.1.1',
        'FORMAT': 'image/png',
        'TRANSPARENT': true,
      },
      crossOrigin: 'anonymous',
    })

    let warned = false
    source.on('tileloaderror', () => {
      if (warned) return
      warned = true
      store.dispatch('toast/show', {
        message: `No se pudieron cargar los tiles de "${layerConfig.name}" — verifica que el servicio WMS esté disponible.`,
        type: 'error'
      })
    })

    return new TileLayer({
      source,
      opacity: layerConfig.opacity !== undefined ? layerConfig.opacity : 1,
      zIndex: 10
    })
  },

  /**
   * GetFeatureInfo sobre una capa WMS agregada por URL (sesión). El INFO_FORMAT viene del
   * GetCapabilities capturado en detect() — algunos servidores (ej. ArcGIS Server) solo
   * soportan "application/geo+json" y no "application/json". null explícito = el servidor no
   * anunció ningún formato JSON. undefined (capas de sesiones antiguas) = fallback histórico.
   */
  async queryFeatures({ layer, coordinate, resolution, projection }) {
    const infoFormat = layer.provider.meta?.infoFormat === undefined ? 'application/json' : layer.provider.meta.infoFormat
    if (infoFormat === null) {
      console.warn(`"${layer.name}" no anuncia ningún INFO_FORMAT compatible con JSON en su GetCapabilities — se omite la consulta.`)
      return { error: true, layerName: layer.name }
    }

    const wmsSource = new TileWMS({
      url: layer.provider.url,
      params: {
        'LAYERS': layer.provider.layerName,
        'VERSION': '1.1.1',
      }
    })

    const featureInfoUrl = wmsSource.getFeatureInfoUrl(
      coordinate,
      resolution,
      projection,
      {
        'INFO_FORMAT': infoFormat,
        'BUFFER': WMS_HIT_BUFFER_PX
      }
    )
    if (!featureInfoUrl) return null

    let response
    try {
      response = await fetchWithTimeout(featureInfoUrl, FEATURE_INFO_TIMEOUT_MS)
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn(`GetFeatureInfo para "${layer.name}" superó el tiempo de espera (${FEATURE_INFO_TIMEOUT_MS / 1000}s).`)
      }
      throw error
    }

    if (!response.ok) {
      console.warn(`GetFeatureInfo falló para "${layer.name}": HTTP ${response.status}`)
      return { error: true, layerName: layer.name }
    }

    // Algunos servidores devuelven 200 OK con un ServiceExceptionReport en XML cuando
    // el INFO_FORMAT pedido no es el que realmente soportan — evita que response.json()
    // explote con un SyntaxError críptico y lo reporta como el error real que es.
    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('json')) {
      console.warn(`GetFeatureInfo para "${layer.name}" no devolvió JSON (Content-Type: ${contentType || 'desconocido'}, INFO_FORMAT pedido: ${infoFormat}).`)
      return { error: true, layerName: layer.name }
    }

    const data = await response.json()
    return {
      layerName: layer.name,
      features: data.features || []
    }
  }
}
